import { toast } from "sonner";
import { useIntegrationApp } from "@membranehq/react";
import { Info, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { VECTOR_OBJECTS } from "../../../../../components/ai-memory/constants";
import FieldConfigurationModal from "../../../../../components/ai-memory/FieldConfigurationModal";
import { Alert, AlertDescription } from "../../../../../components/ui/alert";
import { Badge, type StatusType } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { useAIMemoryObjects, useEnableAIMemoryObject } from "../../../../../hooks/QueryHooks";
import type { AIMemoryObjectType } from "../../../../../services/aiMemory/aiMemoryEndpoints.interface";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";

export interface AIObjectPanelProps {
    slug: string;
    label?: string;
    isConnected: boolean;
    onToggleSelected: (slug: string, enabled: boolean) => void;
    providerKey?: string;
    providerLabel?: string;
    integrationId?: string;
    layout?: "card" | "inline";
}

function AIObjectPanel({
    slug,
    label,
    isConnected,
    onToggleSelected,
    providerKey = "hubspot",
    providerLabel = "HubSpot",
    integrationId,
    layout = "card",
}: AIObjectPanelProps) {
    const workspace: any = useWorkspaceStore((state: any) => state.workspace);
    const integrationApp = useIntegrationApp();

    const aiMemoryObject = useMemo(() => {
        const mappedSlug = slug === "emails" ? "conversation_messages" : slug;
        return VECTOR_OBJECTS.find((object) => object.slug === mappedSlug);
    }, [slug]);

    const mappedSlug = slug === "emails" ? "conversation_messages" : slug;
    const resolvedLabel = useMemo(() => {
        if (label) return label;
        if (mappedSlug === "conversation_messages") return "Customer Conversations";
        return aiMemoryObject?.name || slug;
    }, [label, mappedSlug, aiMemoryObject?.name, slug]);

    const isAIMemorySupported = useMemo(() => {
        if (!aiMemoryObject) return false;
        return aiMemoryObject.sources.includes(providerLabel);
    }, [aiMemoryObject, providerLabel]);

    const [configuringAIMemory, setConfiguringAIMemory] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [flowsEnabled, setFlowsEnabled] = useState(false);
    const [checkingFlowStatus, setCheckingFlowStatus] = useState(false);

    const { data: aiMemoryData, isLoading: aiMemoryLoading, refetch: refetchAIMemory } = useAIMemoryObjects({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id,
    } as any);

    const enableAIMemoryMutation = useEnableAIMemoryObject();

    const aiMemoryStatus = useMemo(() => {
        if (!aiMemoryData?.data) return null;

        const mappedSlug = slug === "emails" ? "conversation_messages" : slug;
        const aiObject = aiMemoryData.data.find((item: any) => item.api_slug === mappedSlug);

        return aiObject ? {
            status: aiObject.status,
            field_count: aiObject.field_count,
            object_id: aiObject.object_id,
            enabled: aiObject.status === "enabled",
        } : { status: "disabled", enabled: false };
    }, [aiMemoryData, slug]);

    const checkFlowStatus = useCallback(async () => {
        if (!integrationId || !aiMemoryStatus?.enabled) {
            setFlowsEnabled(false);
            return;
        }

        setCheckingFlowStatus(true);

        try {
            let flowSlug = slug;
            if (slug === "conversation_messages" && (providerKey === "hubspot" || providerKey === "gmail" || providerKey === "salesforce")) {
                flowSlug = "emails";
            }

            const receiveFlow = `receive-${flowSlug?.endsWith("s") ? flowSlug.slice(0, -1) : flowSlug}-events`;
            const flowData = await integrationApp.connection(providerKey).flow(receiveFlow).get();
            setFlowsEnabled(Boolean(flowData?.enabled));
        } catch (error) {
            console.error("Error checking flow status:", error);
            setFlowsEnabled(false);
        } finally {
            setCheckingFlowStatus(false);
        }
    }, [integrationId, aiMemoryStatus?.enabled, slug, providerKey, integrationApp]);

    useEffect(() => {
        if (aiMemoryStatus?.enabled && integrationId) {
            checkFlowStatus();
        }
    }, [aiMemoryStatus?.enabled, integrationId, checkFlowStatus]);

    const handleEnableAIMemory = useCallback(async () => {
        if (!workspace?.id) return;

        try {
            const mappedSlug = slug === "emails" ? "conversation_messages" : slug;

            await enableAIMemoryMutation.mutateAsync({
                workspace_id: workspace.id,
                object_type: mappedSlug as AIMemoryObjectType,
            });

            toast.success("Enabled", {
                description: `${resolvedLabel} is now powering Customer Intelligence.`,
            });

            await refetchAIMemory();
            onToggleSelected(slug, true);
        } catch (error) {
            toast.error("Could not enable", {
                description: "Customer Intelligence could not be enabled for this data.",
            });
        }
    }, [workspace?.id, slug, resolvedLabel, enableAIMemoryMutation, refetchAIMemory, onToggleSelected]);

    const handleActivateAIMemorySync = useCallback(async () => {
        if (!workspace?.id || !integrationId) return;

        setIsActivating(true);

        let flowSlug = slug;
        if (slug === "conversation_messages" && (providerKey === "hubspot" || providerKey === "gmail" || providerKey === "salesforce")) {
            flowSlug = "emails";
        }

        const firstSyncFlow = `first-sync-for-${flowSlug}`;
        const receiveFlow = `receive-${flowSlug?.endsWith("s") ? flowSlug.slice(0, -1) : flowSlug}-events`;

        toast.loading(`Starting ${resolvedLabel} sync...`, {
            id: `activating-ai-memory-${slug}`,
            description: "Connecting this data to Customer Intelligence",
        });

        try {
            await Promise.all([
                integrationApp.connection(providerKey).flow(firstSyncFlow).get({ autoCreate: true }),
                integrationApp.connection(providerKey).flow(receiveFlow).get({ autoCreate: true }),
            ]);

            await Promise.all([
                integrationApp.connection(providerKey).flow(firstSyncFlow).patch({
                    parameters: { object_type: flowSlug, batch: 0 },
                    enabled: true,
                }),
                integrationApp.connection(providerKey).flow(receiveFlow).patch({
                    parameters: { object_type: flowSlug },
                    enabled: true,
                }),
            ]);

            await integrationApp.connection(providerKey).flow(firstSyncFlow).run({ input: null });
            setFlowsEnabled(true);

            toast.success(`${resolvedLabel} sync is live`, {
                id: `activating-ai-memory-${slug}`,
                description: "Initial sync started. Insights will improve as data arrives.",
            });
        } catch (error) {
            console.error("Error activating AI Memory sync:", error);
            toast.error("Activation failed", {
                id: `activating-ai-memory-${slug}`,
                description: "Could not start data sync for Customer Intelligence.",
            });
        } finally {
            setIsActivating(false);
        }
    }, [workspace?.id, integrationId, slug, resolvedLabel, providerKey, providerLabel, integrationApp]);

    const handleDeactivateAIMemorySync = useCallback(async () => {
        if (!workspace?.id || !integrationId) return;

        setIsActivating(true);

        let flowSlug = slug;
        if (slug === "conversation_messages" && (providerKey === "hubspot" || providerKey === "gmail" || providerKey === "salesforce")) {
            flowSlug = "emails";
        }

        const firstSyncFlow = `first-sync-for-${flowSlug}`;
        const receiveFlow = `receive-${flowSlug?.endsWith("s") ? flowSlug.slice(0, -1) : flowSlug}-events`;

        try {
            await Promise.all([
                integrationApp.connection(providerKey).flow(firstSyncFlow).patch({ enabled: false }),
                integrationApp.connection(providerKey).flow(receiveFlow).patch({ enabled: false }),
            ]);

            setFlowsEnabled(false);

            toast.warning("Sync paused", {
                description: `${resolvedLabel} sync is paused.`,
            });
        } catch (error) {
            console.error("Error deactivating AI Memory sync:", error);
            toast.error("Could not pause sync", {
                description: `Failed to pause ${resolvedLabel} sync.`,
            });
        } finally {
            setIsActivating(false);
        }
    }, [workspace?.id, integrationId, slug, resolvedLabel, providerKey, integrationApp]);

    const isEnabled = Boolean(aiMemoryStatus?.enabled);
    const connectionStatus: StatusType = isConnected ? "active" : "open";
    const syncStatus: StatusType = !isEnabled ? "open" : checkingFlowStatus ? "pending" : flowsEnabled ? "active" : "open";
    const syncStatusLabel = !isEnabled ? "Not enabled" : checkingFlowStatus ? "Checking sync" : flowsEnabled ? "Sync live" : "Sync paused";
    const primaryActionLabel = !isEnabled ? "Enable" : flowsEnabled ? "Pause Sync" : "Start Sync";
    const isPrimaryBusy = !isEnabled ? enableAIMemoryMutation.isLoading : isActivating || checkingFlowStatus;
    const disablePrimaryAction = !isConnected || !isAIMemorySupported || aiMemoryLoading || isPrimaryBusy || !workspace?.id || (isEnabled && !integrationId);
    const configuredFieldCount = aiMemoryStatus?.field_count || 0;
    const hasConfiguredFields = configuredFieldCount > 0;
    const configuredFieldLabel = configuredFieldCount === 1 ? "1 field configured" : `${configuredFieldCount} fields configured`;
    const infoMessage = !isConnected
        ? "Connect this integration first."
        : !isEnabled
            ? "Enable Customer Intelligence, then start sync."
            : checkingFlowStatus
                ? "Checking sync status..."
                : !flowsEnabled
                    ? hasConfiguredFields
                        ? `${configuredFieldLabel}. Sync is paused.`
                        : "Sync is paused. Configure fields anytime for better insights."
                    : hasConfiguredFields
                        ? `Sync is live with ${configuredFieldLabel}.`
                        : "Sync is live. Configure fields anytime to improve insight quality.";

    const renderActions = () => (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Button
                type="button"
                size="sm"
                className="h-7 text-[12px]"
                variant={isEnabled && flowsEnabled ? "outline" : "default"}
                disabled={disablePrimaryAction}
                onClick={() => {
                    if (!isEnabled) {
                        handleEnableAIMemory().catch(console.error);
                        return;
                    }

                    if (flowsEnabled) {
                        handleDeactivateAIMemorySync().catch(console.error);
                    } else {
                        handleActivateAIMemorySync().catch(console.error);
                    }
                }}
            >
                {isPrimaryBusy ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                ) : null}
                {primaryActionLabel}
            </Button>

            <Button
                type="button"
                size="sm"
                className="h-7 text-[12px]"
                variant="outline"
                disabled={!isEnabled}
                onClick={() => setConfiguringAIMemory(true)}
            >
                Configure Fields
            </Button>
        </div>
    );

    if (layout === "inline") {
        return (
            <div className="space-y-2">
                {isAIMemorySupported ? (
                    renderActions()
                ) : (
                    <Alert className="border-status-info/20 bg-status-info/[0.06] py-2.5">
                        <Info className="h-3.5 w-3.5 text-status-info" />
                        <AlertDescription className="text-[11px] leading-4 text-muted-foreground">
                            Customer Intelligence is not available for {providerLabel} on this data type.
                        </AlertDescription>
                    </Alert>
                )}

                <FieldConfigurationModal
                    opened={configuringAIMemory}
                    onClose={() => setConfiguringAIMemory(false)}
                    objectType={slug as AIMemoryObjectType}
                    objectId={aiMemoryStatus?.object_id || null}
                />
            </div>
        );
    }

    return (
        <div className="space-y-3 rounded-lg border border-border p-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-0.5">
                    <h3 className="text-[14px] font-semibold text-foreground">{resolvedLabel}</h3>
                    {aiMemoryObject ? (
                        <p className="text-[12px] leading-4 text-muted-foreground">{aiMemoryObject.description}</p>
                    ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="statusPill" status={connectionStatus} className="w-fit">
                        {isConnected ? "Connected" : "Not connected"}
                    </Badge>
                    {!isAIMemorySupported ? (
                        <Badge variant="outline" className="h-5 text-[10px] text-muted-foreground">
                            Unavailable for {providerLabel}
                        </Badge>
                    ) : aiMemoryLoading ? (
                        <Badge variant="statusPill" status="pending" className="w-fit">
                            Loading
                        </Badge>
                    ) : (
                        <Badge variant="statusPill" status={syncStatus} className="w-fit">
                            {syncStatusLabel}
                        </Badge>
                    )}
                </div>
            </div>

            {isAIMemorySupported ? (
                <div className="space-y-3 border-t border-border/80 pt-3">
                    {renderActions()}

                    <Alert className="border-status-info/20 bg-status-info/[0.06] py-2.5">
                        <Info className="h-3.5 w-3.5 text-status-info" />
                        <AlertDescription className="text-[11px] leading-4 text-muted-foreground">
                            {infoMessage}
                        </AlertDescription>
                    </Alert>
                </div>
            ) : (
                <div className="border-t border-border/80 pt-3">
                    <Alert className="border-status-info/20 bg-status-info/[0.06] py-2.5">
                        <Info className="h-3.5 w-3.5 text-status-info" />
                        <AlertDescription className="text-[11px] leading-4 text-muted-foreground">
                            Customer Intelligence is not available for {providerLabel} on this data type.
                        </AlertDescription>
                    </Alert>
                </div>
            )}

            <FieldConfigurationModal
                opened={configuringAIMemory}
                onClose={() => setConfiguringAIMemory(false)}
                objectType={slug as AIMemoryObjectType}
                objectId={aiMemoryStatus?.object_id || null}
            />
        </div>
    );
}

export default AIObjectPanel;
